import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { contactRequestsService } from '../../services/api';
import useContactRequestsData from './useContactRequestsData';
import useContactRequestColumns from './useContactRequestColumns';
import useContactRequestsTour from './useContactRequestsTour';
import ContactRequestsFilters from './ContactRequestsFilters';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import TablePagination from '../common/TablePagination';
import HorizontalTableNavigation from '../common/HorizontalTableNavigation';
import useHorizontalScroll from '../../hooks/useHorizontalScroll';
import useColumnPinning from '../../hooks/useColumnPinning';
import useCountryUtils from '../../hooks/useCountryUtils';
import useProfileUtils from '../../hooks/useProfileUtils';

const ContactRequests = ({ hasSeenNavbarTour }) => {
  const { isAuthenticated, userInfo } = useAuth();
  const isAdmin = userInfo?.role === 'admin';

  // Use custom hook for data management
  const {
    requests,
    totalCount,
    pageCount,
    countries,
    profiles,
    loading,
    error,
    pagination,
    setPagination,
    serverFilters,
    filterInputs,
    handleInputChange,
    applyFilters,
    clearAllFilters,
    parseDateString,
    formatDateForAPI
  } = useContactRequestsData({ isAuthenticated, isAdmin });

  // Use tour hook
  const { startTour } = useContactRequestsTour({
    isAuthenticated,
    dataReady: requests.length > 0,
    hasSeenNavbarTour
  });

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const downloadDropdownRef = useRef(null);

  // Horizontal scroll pagination
  const tableContainerRef = useRef(null);
  const {
    horizontalPages,
    currentHorizontalPage,
    showHorizontalPager,
    canScrollRight,
    scrollToHorizontalPage,
    handleArrowClick
  } = useHorizontalScroll(tableContainerRef, [requests]);

  // Column pinning hook
  const {
    columnPinning,
    setColumnPinning,
    getCommonPinningStyles,
  } = useColumnPinning(tableContainerRef, [requests]);

  // Use shared utility hooks for dropdown transformations
  const { countryItems } = useCountryUtils(countries);
  const { profileItems } = useProfileUtils(profiles);

  // Use column definitions hook
  const { columns } = useContactRequestColumns({ isAdmin, countries });

  // Close download dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle download with format option
  const handleDownload = (format) => {
    const url = contactRequestsService.getDownloadUrl({
      fromDate: serverFilters.fromDate,
      toDate: serverFilters.toDate,
      country: serverFilters.country,
      profile: serverFilters.profile,
      user: serverFilters.user,
      phone: serverFilters.phone,
      postCode: serverFilters.postCode,
      recommendationId: serverFilters.recommendationId,
      comments: serverFilters.comments,
      format
    });
    window.location.href = url;
    setShowDownloadDropdown(false);
  };

  // Wrapper for pagination change to reset pageIndex when pageSize changes
  const handlePaginationChange = (updater) => {
    setPagination(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // If page size changed, reset to first page
      if (next.pageSize !== prev.pageSize) {
        return { ...next, pageIndex: 0 };
      }
      return next;
    });
  };

  // TanStack Table instance with manual pagination
  const table = useReactTable({
    data: requests,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: pageCount,
    onPaginationChange: handlePaginationChange,
    enableColumnPinning: true,
    onColumnPinningChange: setColumnPinning,
    state: {
      pagination,
      columnPinning,
    },
  });

  return (
    <div className="px-4 py-8">
      {/* Header Section */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Contact Requests
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View and manage contact requests from users.
          </p>
        </div>
        {/* Tour Button */}
        <button
          onClick={startTour}
          className="hidden md:flex flex-col items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
          data-intro="tour-button"
        >
          <svg
            className="w-5 h-5 mb-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
          <span className="text-xs font-medium">Tour</span>
        </button>
      </div>

      {/* Main Content Section with Loading Overlay */}
      <div className="relative">
        {/* Filter Section */}
        <ContactRequestsFilters
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          filterInputs={filterInputs}
          handleInputChange={handleInputChange}
          countries={countries}
          profiles={profiles}
          isAdmin={isAdmin}
          applyFilters={applyFilters}
          clearAllFilters={clearAllFilters}
          parseDateString={parseDateString}
          formatDateForAPI={formatDateForAPI}
          loading={loading}
          countryItems={countryItems}
          profileItems={profileItems}
        />

        {/* Download Button - Below filters */}
        {!loading && requests.length > 0 && (
          <div className="mb-4 flex justify-end">
            <div className="relative" ref={downloadDropdownRef} data-intro="download-button">
              <button
                onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 rounded-lg dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span>Download</span>
                <svg className={`w-3 h-3 transition-transform ${showDownloadDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showDownloadDropdown && (
                <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    <button
                      onClick={() => handleDownload('xlsx')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>Excel (.xlsx)</span>
                    </button>
                    <button
                      onClick={() => handleDownload('csv')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span>CSV (.csv)</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error State - Retry Component (matching ProductUsageReport pattern) */}
        {error && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              Unable to load data
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {error}
            </p>
            <button
              onClick={applyFilters}
              className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Data Table - Always visible so loading overlay can blur it */}
        {!error && (
          <>
            {/* Navigation Above Table */}
            <HorizontalTableNavigation
              showHorizontalPager={showHorizontalPager}
              currentHorizontalPage={currentHorizontalPage}
              horizontalPages={horizontalPages}
              scrollToHorizontalPage={scrollToHorizontalPage}
              handleArrowClick={handleArrowClick}
              canScrollRight={canScrollRight}
              position="above"
              variant="both"
            />

            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden ${showHorizontalPager ? 'border-t-0 sm:rounded-b-lg' : 'sm:rounded-lg'}`} data-intro="contact-requests-table">
              {/* Table */}
              <div ref={tableContainerRef} className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-200 dark:bg-gray-700 dark:text-gray-400">
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                          <th
                            key={header.id}
                            scope="col"
                            data-column-id={header.id}
                            className={`px-6 py-3 whitespace-nowrap group relative ${
                              header.column.getIsPinned() === 'left'
                                ? 'bg-gray-200 dark:bg-gray-700'
                                : ''
                            }`}
                            style={{
                              width: header.column.getSize() !== 150 ? header.column.getSize() : undefined,
                              minWidth: header.column.columnDef.minSize,
                              ...getCommonPinningStyles(header.column, table)
                            }}
                          >
                            <div className="flex items-center">
                              <span>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </span>
                              <button
                                onClick={() => {
                                  const isPinned = header.column.getIsPinned();
                                  if (isPinned === 'left') {
                                    header.column.pin(false);
                                  } else {
                                    header.column.pin('left');
                                  }
                                }}
                                className={`hidden md:block ml-2 p-1 rounded hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none transition-opacity ${
                                  header.column.getIsPinned() === 'left'
                                    ? 'opacity-100'
                                    : 'opacity-0 group-hover:opacity-100'
                                }`}
                                title={header.column.getIsPinned() === 'left' ? 'Unpin column' : 'Pin column to left'}
                              >
                                <svg
                                  className={`w-4 h-4 ${
                                    header.column.getIsPinned() === 'left'
                                      ? 'text-blue-600 dark:text-blue-400'
                                      : 'text-gray-400 dark:text-gray-500'
                                  }`}
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12M8.8,14L10,12.8V4H14V12.8L15.2,14H8.8Z"/>
                                </svg>
                              </button>
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                          No contact requests found
                        </td>
                      </tr>
                    ) : (
                      table.getRowModel().rows.map(row => (
                        <tr
                          key={row.id}
                          className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          {row.getVisibleCells().map((cell, cellIndex) => (
                            <td
                              key={cell.id}
                              className={`px-6 py-4 whitespace-nowrap ${cellIndex === 0 ? 'font-medium text-gray-900 dark:text-white' : ''} ${
                                cell.column.getIsPinned() === 'left'
                                  ? 'bg-white dark:bg-gray-800'
                                  : ''
                              }`}
                              title={String(cell.getValue() || '')}
                              style={getCommonPinningStyles(cell.column, table)}
                            >
                              <span className="block max-w-xs overflow-hidden overflow-ellipsis">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </span>
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Table Navigation and Pagination Container */}
              <div className="bg-gray-50/50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
                {/* Horizontal Navigation Below Table */}
                <HorizontalTableNavigation
                  showHorizontalPager={showHorizontalPager}
                  currentHorizontalPage={currentHorizontalPage}
                  horizontalPages={horizontalPages}
                  scrollToHorizontalPage={scrollToHorizontalPage}
                  handleArrowClick={handleArrowClick}
                  canScrollRight={canScrollRight}
                  position="below"
                  variant="both"
                />

                {/* Pagination */}
                <div className="px-4 py-3 flex items-center justify-between" data-intro="pagination">
                  <TablePagination table={table} totalCount={totalCount} />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactRequests;
